import { Grid, Icons, Stack } from '@/theme/index';
import {
  SectionTitle,
  TallStack,
  ThreatItem,
  TileItem,
  VerticalDivider,
  GridItem,
} from '../components';
import {
  getFraudTileText,
  getThreatLevel,
  getThreatLevelTitle,
} from '../utils';

export interface FraudUrlOverviewProps {
  domain: string;
  isSuspicious?: boolean;
  isUnsafe?: boolean;
  ipCountryCode: string;
  pageTitle: string;
  siteCategory: string;
  isRedirected?: boolean;
  finalUrl: string;
}

export function FraudUrlOverview({
  domain,
  isSuspicious,
  isUnsafe,
  ipCountryCode,
  pageTitle,
  siteCategory,
  isRedirected,
  finalUrl,
}: FraudUrlOverviewProps) {
  return (
    <Stack>
      <SectionTitle>Overview</SectionTitle>

      <Grid container spacing={1}>
        <GridItem>
          <Stack gap={1}>
            <TileItem
              title="Domain:"
              text={domain}
              IconComponent={Icons.Info}
              testLabelId="url-overview-domain-label"
              testContentId="url-overview-domain"
            />
            <TileItem
              title="IP Country Code:"
              text={ipCountryCode}
              IconComponent={Icons.Flag}
              testLabelId="url-overview-ip-country-code-label"
              testContentId="url-overview-ip-country-code"
            />
          </Stack>
        </GridItem>

        <GridItem>
          <TallStack
            direction="row"
            spacing={2}
            height="100%"
            alignItems="center"
            data-cy="url-overview-threat-detection"
          >
            <ThreatItem
              text={getThreatLevelTitle(
                isSuspicious,
                'Suspicious',
                'Not Suspicious',
                'Suspicious Behavior Unknown'
              )}
              tooltipText="Indicates whether this URL is suspected of being malicious or used for phishing or abuse."
              threatLevel={getThreatLevel(isSuspicious)}
              width="100%"
            />
            <VerticalDivider />
            <ThreatItem
              text={getThreatLevelTitle(
                isUnsafe,
                'Unsafe',
                'Safe',
                'Safety Level Unknown'
              )}
              tooltipText="Indicates whether this domain is suspected of being unsafe due to phishing, malware, spamming, or abusive behavior. "
              threatLevel={getThreatLevel(isUnsafe)}
              width="100%"
            />
          </TallStack>
        </GridItem>

        <GridItem>
          <TileItem
            title="Page Title:"
            text={pageTitle}
            IconComponent={Icons.Monitor}
            testLabelId="url-overview-page-title-label"
            testContentId="url-overview-page-title"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Site Category:"
            text={siteCategory}
            IconComponent={Icons.Monitor}
            testLabelId="url-overview-site-category-label"
            testContentId="url-overview-site-category"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="URL Redirects:"
            text={getFraudTileText(isRedirected)}
            IconComponent={Icons.Monitor}
            tooltipText="Indicates if the URL automatically redirects to another URL. Attackers often use redirects to conceal the final destination of a malicious link, making it harder to detect and block harmful content."
            testLabelId="url-overview-url-redirects-label"
            testContentId="url-overview-url-redirects"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Final URL (after redirects):"
            text={finalUrl}
            IconComponent={Icons.Monitor}
            tooltipText="Indicates the final destination after all automatic redirects have finished. This is the URL a user would land on if the scanned URL was clicked."
            testLabelId="url-overview-final-url-label"
            testContentId="url-overview-final-url"
          />
        </GridItem>
      </Grid>
    </Stack>
  );
}
